import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { PriceList } from '@/api/generated/models';
import {
  getGetPriceListQueryKey,
  getListPriceListsQueryKey,
  getSearchPriceListsQueryKey,
  useCreatePriceList,
  useListPriceLists,
  useUpdatePriceList
} from '@/api/generated/price-lists/price-lists';
import {
  DateField,
  FormModal,
  ModalFooter,
  MutationButton,
  NumberField,
  SelectField,
  TextareaField,
  TextField,
  Toast
} from '@/components/common';

// DatePickerInput (@mantine/dates v8) radi s ISO date stringovima
// ("YYYY-MM-DD"), ne s Date objektima — to je i format koji backend očekuje,
// pa se ovdje nigdje ne konvertira u/iz Date-a.
const baseSchema = z.object({
  parentId: z.string().optional(),
  coefficient: z.number().nullable(),
  name: z.string().min(1),
  description: z.string().optional(),
  plType: z.enum(['BASE', 'CONTRACT', 'SPECIAL']),
  validFrom: z.string().min(1),
  validTo: z.string().nullable().optional()
});

type FormValues = z.infer<typeof baseSchema>;

// Isti obrazac kao RegisterForm/ConnectForm/PartnerLinkingPage — backend već
// vraća specifičan, lokalizirani razlog (npr. nevažeći datumski raspon).
const priceListErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

const today = () => new Date().toISOString().slice(0, 10);

interface PriceListFormModalProps {
  opened: boolean;
  onClose: () => void;
  priceList?: PriceList;
  onCreated?: (id: string) => void;
  onSaved?: (message: string) => void;
}

export const PriceListFormModal = ({
  opened,
  onClose,
  priceList,
  onCreated,
  onSaved
}: PriceListFormModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!priceList;

  const { data: priceListsData } = useListPriceLists();
  const parentOptions = (priceListsData ?? [])
    .filter((pl) => pl.isActive)
    .map((pl) => ({ value: pl.id ?? '', label: pl.name ?? '—' }));

  const schema = baseSchema.superRefine((data, ctx) => {
    if (data.validTo && data.validTo < data.validFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['validTo'],
        message: t('priceLists.validToBeforeValidFrom')
      });
    }
    if (data.coefficient !== null && data.coefficient <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['coefficient'],
        message: t('priceLists.invalidCoefficient')
      });
    }
  });

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: {
      parentId: '',
      coefficient: null,
      name: '',
      description: '',
      plType: 'BASE',
      validFrom: today(),
      validTo: null
    }
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        parentId: '',
        coefficient: null,
        name: priceList?.name ?? '',
        description: priceList?.description ?? '',
        plType: priceList?.plType ?? 'BASE',
        validFrom: priceList?.validFrom ?? today(),
        validTo: priceList?.validTo ?? null
      });
    }
  }, [opened, priceList]);

  // Odabir podloge kopira njezine podatke u formu kao polazište (korisnik ih
  // i dalje može izmijeniti prije spremanja) — same stavke se materijaliziraju
  // tek na backendu, jednom, u trenutku kreiranja (vidi PriceListService.create),
  // pomnožene koeficijentom ako je unesen.
  const handleParentChange = (value: string | null) => {
    const base = priceListsData?.find((pl) => pl.id === value);
    if (value && base) {
      form.setValues({
        parentId: value,
        coefficient: null,
        name: base.name ?? '',
        description: base.description ?? '',
        plType: form.values.plType,
        validFrom: base.validFrom ?? today(),
        validTo: base.validTo ?? null
      });
    } else {
      form.setValues({ parentId: value ?? '', coefficient: null });
    }
  };

  const createMutation = useCreatePriceList({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListPriceListsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchPriceListsQueryKey() });
        const createdId = (created as PriceList | undefined)?.id;
        onSaved?.(t('priceLists.createSuccess'));
        if (createdId) onCreated?.(createdId);
      }
    }
  });

  const updateMutation = useUpdatePriceList({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListPriceListsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchPriceListsQueryKey() });
        if (priceList?.id) {
          queryClient.invalidateQueries({ queryKey: getGetPriceListQueryKey(priceList.id) });
        }
        onSaved?.(t('priceLists.updateSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    // Klik na "clear" (X) na DatePickerInputu ponekad vraća '' umjesto null —
    // oboje mora značiti "bez datuma završetka" (backend LocalDate ne
    // prihvaća prazan string, samo izostavljeno polje ili null).
    const validTo = values.validTo && values.validTo.trim() !== '' ? values.validTo : undefined;

    if (isEdit && priceList?.id) {
      updateMutation.mutate({
        id: priceList.id,
        data: {
          name: values.name,
          description: values.description,
          validFrom: values.validFrom,
          validTo
        }
      });
    } else {
      createMutation.mutate({
        data: {
          name: values.name,
          description: values.description,
          plType: values.plType,
          parentId: values.parentId || undefined,
          coefficient: values.parentId ? (values.coefficient ?? undefined) : undefined,
          validFrom: values.validFrom,
          validTo
        }
      });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? t('priceLists.editPriceList') : t('priceLists.newPriceList')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
            {t('priceLists.formIntro')}
          </Text>

          {mutation.isError && (
            <Toast tone="danger">{priceListErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          {!isEdit && parentOptions.length > 0 && (
            <SelectField
              label={t('priceLists.fieldParent')}
              placeholder={t('priceLists.fieldParentPlaceholder')}
              clearable
              data={parentOptions}
              value={form.values.parentId || null}
              onChange={handleParentChange}
            />
          )}

          {!isEdit && form.values.parentId && (
            <NumberField
              label={t('priceLists.fieldCoefficient')}
              helperText={t('priceLists.fieldCoefficientHelp')}
              placeholder="1.00"
              allowNegative={false}
              decimalScale={2}
              {...form.getInputProps('coefficient')}
            />
          )}

          <TextField label={t('priceLists.fieldName')} required {...form.getInputProps('name')} />

          <SelectField
            label={t('priceLists.fieldType')}
            required
            allowDeselect={false}
            disabled={isEdit}
            data={[
              { value: 'BASE', label: t('priceLists.typeBase') },
              { value: 'CONTRACT', label: t('priceLists.typeContract') },
              { value: 'SPECIAL', label: t('priceLists.typeSpecial') }
            ]}
            {...form.getInputProps('plType')}
          />

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <DateField
                label={t('priceLists.fieldValidFrom')}
                required
                valueFormat="DD.MM.YYYY."
                maxDate={form.values.validTo ?? undefined}
                {...form.getInputProps('validFrom')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <DateField
                label={t('priceLists.fieldValidTo')}
                valueFormat="DD.MM.YYYY."
                clearable
                minDate={form.values.validFrom}
                {...form.getInputProps('validTo')}
              />
            </div>
          </div>

          <TextareaField
            label={t('priceLists.fieldDescription')}
            minRows={3}
            {...form.getInputProps('description')}
          />

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {t('priceLists.savePriceList')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
